import JSZip from 'jszip';
import api from '@server/api-config';
import env from '@environment';
import { createAsyncThunk } from '@reduxjs/toolkit';
import { AppDispatch, RootState } from './store';
import { WithValidationResult } from '@app/shared/interfaces/with-validation-result';
import { EndpointConstants } from './EndpointConstants';
import { CalcMethod } from '@app/shared/models/contracts/enums/shared-enums';
import * as notificationActions from './notification-slice';
import { SeverityType } from '@app/shared/mui-components/alert/AlertTypes';
import { l } from '@locale/setupI18n';
import { OpmCalculationType } from './opm-calculation-slice';
import { runOpmCalculationAndAwaitResult } from '@app/core/signalr/signalRHubConnection';

interface CalculationExportRequest {
  fileName: string;
  endpoint: EndpointConstants;
}

const normalizeProjectName = (name: string): string =>
  name
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 64);

const downloadBlob = (blob: Blob, filename: string) => {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  window.URL.revokeObjectURL(url);
};

export const exportAiPackage = createAsyncThunk<
  void,
  void,
  { state: RootState; dispatch: AppDispatch }
>('export/aiPackage', async (_, thunkAPI) => {
  const project = thunkAPI.getState().project.projectDraft;
  const userId = thunkAPI.getState().userInfo.userInfo.id;
  const payload = JSON.stringify(project);
  const isOpmProject = project.details.calcMethod !== CalcMethod.PWERM;
  const isPwermProject = project.details.calcMethod !== CalcMethod.OPM;

  const requests: CalculationExportRequest[] = [
    { fileName: 'build-structure.json', endpoint: EndpointConstants.CalculateBuildStructure },
    { fileName: 'capital-structure.json', endpoint: EndpointConstants.CalculateCapitalStructure },
  ];

  if (isPwermProject) {
    requests.push(
      { fileName: 'pwerm-inputs.json', endpoint: EndpointConstants.CalculatePwermInputs },
      { fileName: 'pwerm.json', endpoint: EndpointConstants.CalculatePwerm }
    );
  }

  if (isOpmProject) {
    requests.push({ fileName: 'opm-inputs.json', endpoint: EndpointConstants.CalculateOpmInputs });
  }

  try {
    const responses = await Promise.all(
      requests.map(({ endpoint }) =>
        api.post<WithValidationResult<unknown>>(`${env.apiUrl}${endpoint}`, payload)
      )
    );

    const zip = new JSZip();
    zip.file('project.json', JSON.stringify(project, null, 2));
    responses.forEach((response, index) => {
      zip.file(requests[index].fileName, JSON.stringify(response.data.result, null, 2));
    });

    // OPM results are pushed back over SignalR rather than returned from a REST call.
    if (isOpmProject) {
      const riskFreeResults = await runOpmCalculationAndAwaitResult(
        project.id,
        userId,
        project,
        OpmCalculationType.RiskFree
      );
      zip.file('opm-risk-free.json', JSON.stringify(riskFreeResults, null, 2));

      if (project.opmInput.realWorldRate !== null) {
        const realWorldResults = await runOpmCalculationAndAwaitResult(
          project.id,
          userId,
          project,
          OpmCalculationType.RealWorld
        );
        zip.file('opm-real-world.json', JSON.stringify(realWorldResults, null, 2));
      }
    }

    zip.file('README.txt', l('_AiPackageReadme'));

    const blob = await zip.generateAsync({ type: 'blob' });
    const normalizedProjectName = normalizeProjectName(project.name ?? '');
    const filenameSuffix = normalizedProjectName ? `-${normalizedProjectName}` : '';
    downloadBlob(blob, `ai-package-${project.id}${filenameSuffix}.zip`);
  } catch (err) {
    thunkAPI.dispatch(
      notificationActions.showNotificationSnackbar({
        severity: SeverityType.error,
        message: l('_FailedToGenerateAiPackage'),
        autoHide: true,
      })
    );
    throw err;
  }
});
